/**
 * @hotelb2b/legal-content
 *
 * Single source of truth for legal documents (privacy policy, terms of
 * service) used across kukla.tech sp. z o.o. platforms (KiedyNabory.pl,
 * HotelB2B), scoped per brand (ADR-088).
 *
 * See ADR-075 for the distribution mechanism, ADR-088 for the per-brand
 * dimension. New code should resolve brand-scoped content through
 * `getLegalContent(brandSlug)` (the registry) rather than the deprecated
 * KN-only free functions kept below for backward compatibility.
 *
 * BROWSER-SAFE BARREL — this file MUST NOT transitively import `node:fs` or
 * anything that reads from disk. It is bundled into the frontend (webpack 5 /
 * react-scripts, no Node polyfills) as well as the backend and both Astro
 * landings. For fs-reading / build-time-only helpers, use the server-only
 * entry point `@hotelb2b/legal-content/server` (`src/server.ts`) instead —
 * NEVER re-export those from here (incident 2026-07-03:
 * `UnhandledSchemeError: Reading from "node:fs"` broke the frontend build
 * when `getSanitizedLegalHtml`'s fs-reading implementation was re-exported
 * from this barrel).
 */

export { LEGAL_VERSIONS, LEGAL_VERSIONS_BY_BRAND } from './version';
export type { LegalVersion } from './version';

export {
  WITHDRAWAL_WAIVER_VERSION,
  WITHDRAWAL_WAIVER_LABEL,
} from './withdrawal-waiver';

export { BRAND_SLUGS, FALLBACK_BRAND_SLUG, isKnownBrandSlug } from './brand';
export type { BrandSlug } from './brand';

export {
  LEGAL_VERSION_HISTORY,
  LEGAL_DOCUMENT_TYPES,
  LEGAL_DOCUMENT_LABELS,
  LegalVersionMismatchError,
  assertVersionIsCurrent,
  assertVersionIsCurrentForBrand,
  isVersionCurrent,
  currentVersion,
  getCurrentVersionEntry,
  getArchivedVersions,
  selectArchivedVersions,
  formatLegalDatePl,
} from './version-history';
export type {
  LegalDocumentType,
  LegalVersionHistoryEntry,
} from './version-history';

export {
  LEGAL_DOCUMENT_URLS,
  LEGAL_DOCUMENT_URLS_BY_BRAND,
  resolvePendingFromCache,
} from './pending';
export type {
  PendingDoc,
  UserVersionCache,
  LegalStateDocEntry,
  LegalStateBrandEntry,
} from './pending';

export { getLegalContent } from './registry';
export type { BrandLegalContent } from './registry';

import { SANITIZED_LEGAL_HTML } from './generated/sanitized-legal-html';
import { LegalDocumentType } from './version-history';
import { FALLBACK_BRAND_SLUG } from './brand';

export { SANITIZED_LEGAL_HTML };

/**
 * @deprecated KN-only backward-compat accessor for the pre-sanitized HTML of
 * the CURRENT version of `documentType` (spec 2026-07-03-legal-consent-soft-modal,
 * anti-H1). Backed by the build-time generated constant above (`src/generated/`)
 * — zero Node built-ins, safe to import from webpack/CRA-bundled frontend
 * code. New code should use `getLegalContent(brandSlug).getSanitizedHtml(docType)`
 * instead (ADR-088).
 */
export function getSanitizedLegalHtml(documentType: LegalDocumentType): string {
  return SANITIZED_LEGAL_HTML[FALLBACK_BRAND_SLUG][documentType];
}

/**
 * Paths to KiedyNabory's legal content markdown files, relative to the
 * package root. Use these paths for build-time imports in Astro SSG or for
 * path resolution.
 *
 * @deprecated KN-only. For per-brand content paths use
 * `getLegalContent(brandSlug).getCurrentVersionEntry(docType).contentPath`.
 */
export const LEGAL_CONTENT_PATHS = {
  privacyPolicy: 'content/kiedynabory/privacy.md',
  termsOfService: 'content/kiedynabory/terms.md',
} as const;

export type LegalContentKey = keyof typeof LEGAL_CONTENT_PATHS;
